import { AsyncPipe, CommonModule, NgClass, NgComponentOutlet } from '@angular/common';
import { Component, Inject, InjectionToken, Injector, OnInit, Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { tracking } from '@ecs-helpers';
import { TranslatePipe } from '@ngx-translate/core';
import { TrackingService } from '@os-components';
import { SewButton, SewWizard, SewWizardItem } from '@sew/ui-components-angular/standalone';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { BasketItem } from '../../../services/basket.service';
import { ModalCloseEvent } from '../../../services/modal/modal-ref';
import { InjectionData, MODAL_CONTENT_TOKEN, ModalReturnType } from '../../../services/modal/modal.helper';
import { CableConfigurationComponent } from './components/cable-configuration/cable-configuration.component';
import { ECoverConfigurationComponent } from './components/e-cover-configuration/e-cover-configuration.component';
import { GearOilConfigurationComponent } from './components/gear-oil-configuration/gear-oil-configuration.component';
import { PartConfigurationWizardService, StoreStepItems } from './part-configuration-wizard.service';

export enum StepType {
    E_COVER = 'E_COVER',
    GEAR_OIL = 'GEAR_OIL',
    CABLE = 'CABLE',
}

type Constructor<T = any> = Type<T>;
type FirstParameter<F extends Constructor> = ConstructorParameters<F>[0];

/**
 * Type of data that gets injected with InjectionData
 */
export type ClassInjectionData<F extends Constructor> =
    FirstParameter<F> extends InjectionDataStep<infer Input> ? Input : never;

// TODO change data to any or the different possibilities and remove the test property
export interface WizardStep<C extends Constructor> {
    component: C;
    data: { title: string; stepData: ClassInjectionData<C>; stepTrackingPoint: string };
    finished: boolean;
}

export interface PartConfigurationData {
    steps: WizardStep<DialogComponent>[];
    isLoggedIn: boolean;
}

// TODO Add dynamic second type
export const WIZARD_STEP_TOKEN = new InjectionToken<InjectionDataStep<any>>('WizardStepToken');

export type InjectionDataStep<Input, Output = StoreStepItems> = {
    stepData: Input;
    storedData?: StoreStepItems;
    valid: Subject<boolean>;
    nextStep: Observable<void>;
    addBasketItems: (returnData: Output) => void;
};

/**
 * Add all the possibly injected components
 */
export type DialogComponent = Type<
    GearOilConfigurationComponent | ECoverConfigurationComponent | CableConfigurationComponent
>;

export type XSellingWizardOutput = ModalCloseEvent<ModalReturnType<typeof PartConfigurationWizardComponent>>;

@Component({
    selector: 'ecs-part-configuration-wizard',
    templateUrl: './part-configuration-wizard.component.html',
    styleUrls: ['./part-configuration-wizard.component.scss'],
    imports: [
        NgComponentOutlet,
        NgClass,
        AsyncPipe,
        TranslatePipe,
        SewWizard,
        SewWizardItem,
        SewButton,
        CommonModule,
        FormsModule,
    ],
})
export class PartConfigurationWizardComponent implements OnInit {
    public component!: DialogComponent;
    public contentInjector!: Injector;
    public steps: WizardStep<DialogComponent>[] = [];
    public stepIndex = 0;

    private validSubject = new BehaviorSubject<boolean>(false);
    private nextStepSubject = new Subject<void>();
    private readonly nextStep = this.nextStepSubject.asObservable();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly valid = this.validSubject.asObservable();

    public constructor(
        // TODO second parameter of injection has to be changed to an object containing the step results
        @Inject(MODAL_CONTENT_TOKEN)
        private injection: InjectionData<PartConfigurationData, BasketItem[]>,
        private injector: Injector,
        private trackingService: TrackingService,
        private partConfigurationWizardService: PartConfigurationWizardService
    ) {}

    public ngOnInit() {
        if (this.injection.data?.steps) {
            this.steps = this.injection.data.steps;
        }
        if (this.steps.length > 0) {
            this.displayStep(this.steps[0]);
        }
    }

    public back() {
        this.displayStep(this.steps[--this.stepIndex]);
    }

    public next() {
        this.trackingService.trackEvent(tracking.step.wizard.next);
        this.nextStepSubject.next();
        this.displayStep(this.steps[++this.stepIndex]);
    }

    public close() {
        this.trackingService.trackEvent(tracking.step.wizard.addToCard);
        this.nextStepSubject.next();
        // The injection close needs to return data in order to react in the `afterClosed$` (see `part-table.component.ts`).
        // If no data is present, it is assumed, that the dialog was closed (X) and therefore the click gets tracked
        // in `part-table.component.ts`.
        const basketItems = this.gatherBasketItemsFromStore();
        this.clearStorage();
        this.injection.close(basketItems);
    }

    private gatherBasketItemsFromStore() {
        let basketItems: BasketItem[] = [];
        this.partConfigurationWizardService.storeditems.forEach((storedItem) => {
            Object.entries(storedItem.items).forEach(
                ([_, basketItemRows]) =>
                    (basketItems = [
                        ...basketItems,
                        ...basketItemRows.reduce(
                            (bItems, basketItemRow) => [...bItems, basketItemRow.basketItem],
                            [] as BasketItem[]
                        ),
                    ])
            );
        });

        return basketItems;
    }

    private displayStep<C extends Constructor>(step: WizardStep<C>) {
        this.validSubject.next(false);
        this.trackingService.trackEvent(step.data.stepTrackingPoint);
        this.component = step.component;
        const injectionData: InjectionDataStep<any> = {
            stepData: step.data.stepData,
            storedData: this.partConfigurationWizardService.storeditems.find(
                (storedItem) => storedItem.step.component === step.component
            )?.items,
            valid: this.validSubject,
            nextStep: this.nextStep,
            addBasketItems: (items: StoreStepItems) => this.storeItems(step, items),
        };

        this.contentInjector = Injector.create({
            providers: [
                {
                    provide: WIZARD_STEP_TOKEN,
                    useValue: injectionData,
                },
            ],
            parent: this.injector,
        });
    }

    private storeItems(step: WizardStep<DialogComponent>, items: StoreStepItems) {
        this.partConfigurationWizardService.addStepItems({ step, items });
    }

    private clearStorage() {
        this.partConfigurationWizardService.clearStorage();
    }
}
